import { useEffect } from "react";
import LegalFooter from "@/components/LegalFooter";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy — Comms Fusion Pro";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      "content",
      "Privacy Policy for Comms Fusion Pro — how we collect, use, and protect your data."
    );
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + "/privacy";
  }, []);

  const updated = "May 5, 2026";

  return (
    <>
    <main className="min-h-screen bg-background text-foreground py-12 px-6">
      <article className="max-w-3xl mx-auto prose prose-neutral dark:prose-invert">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {updated}</p>

        <section className="space-y-6 text-sm leading-relaxed">
          <p>
            Comms Fusion Pro ("we", "our", "the Service") is a WhatsApp Business
            communication and automation platform. This Privacy Policy explains what
            information we collect, how we use it, and the choices you have.
          </p>

          <h2 className="text-xl font-semibold mt-8">1. Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account data:</strong> name, email, workspace, authentication identifiers.</li>
            <li><strong>WhatsApp Business data:</strong> business account ID, phone number ID, display name, access tokens you authorize via Meta's Embedded Signup.</li>
            <li><strong>Conversation data:</strong> messages, contacts, media exchanged through the WhatsApp Business API to deliver the Service.</li>
            <li><strong>Usage data:</strong> logs, device/browser info, timestamps for security and reliability.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">2. How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>To provide messaging, automation, analytics, and CRM features.</li>
            <li>To authenticate users and secure workspaces.</li>
            <li>To comply with Meta / WhatsApp Business policies and applicable law.</li>
            <li>To improve reliability and prevent abuse.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">3. Sharing</h2>
          <p>
            We do not sell personal data. We share data only with: (a) Meta/WhatsApp
            to deliver messages, (b) infrastructure providers (hosting, database) under
            contract, and (c) authorities when legally required.
          </p>

          <h2 className="text-xl font-semibold mt-8">4. Data Retention</h2>
          <p>
            We retain account and conversation data while your workspace is active.
            You may request deletion at any time via the contact email below; data is
            removed within 30 days, subject to legal retention requirements.
          </p>

          <h2 className="text-xl font-semibold mt-8">5. Security</h2>
          <p>
            We use encryption in transit, role-based access control, and row-level
            security on our database. No system is 100% secure; we work continuously
            to protect your information.
          </p>

          <h2 className="text-xl font-semibold mt-8">6. Your Rights</h2>
          <p>
            Depending on your jurisdiction (GDPR, CCPA, etc.), you may have the right
            to access, correct, export, or delete your personal data, and to object to
            certain processing. Contact us to exercise these rights.
          </p>

          <h2 className="text-xl font-semibold mt-8">7. Children</h2>
          <p>The Service is not directed to children under 16.</p>

          <h2 className="text-xl font-semibold mt-8">8. Changes</h2>
          <p>
            We may update this Policy. Material changes will be communicated via the
            app or email. Continued use after changes constitutes acceptance.
          </p>

          <h2 className="text-xl font-semibold mt-8">9. Contact</h2>
          <p>
            Questions or requests:{" "}
            <a href="mailto:support@klicko.org" className="text-primary underline">
              support@klicko.org
            </a>
          </p>
        </section>
      </article>
    </main>
    <LegalFooter />
    </>
  );
}
